/**
 * One settings section, rendered under a header the section itself does not
 * have to draw.
 *
 * Every editor used to open with its own Card and its own title repeating the
 * tab you had just clicked. The header comes from the section registry now, so
 * the heading, the one-line explanation of what the setting governs, and the
 * page's primary action all sit in the same place on every section, and the
 * editors below are only the setting.
 */
import { Link, Navigate, useOutletContext, useParams } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { GradeLevelsEditor } from './GradeLevelsEditor';
import { PaymentsTab } from './PaymentsTab';
import GradingTemplatesEditor from './GradingTemplatesEditor';
import GradeScaleEditor from './GradeScaleEditor';
import ParentAlertsEditor from './ParentAlertsEditor';
import ClassroomsEditor from '../Schedules/ClassroomsEditor';
import SchoolYearsEditor from './SchoolYearsEditor';
import AccountSettings from './AccountSettings';
import { TuitionAndFees } from './TuitionAndFees';
import { EnrollmentChecklists } from './EnrollmentChecklists';
import { SettingsLoading, SettingsPanelHeader } from './SettingsPanel';
import { settingsPath, settingsSection } from './settingsSections';
import type { SettingsOutletContext } from './SettingsLayout';

export function SettingsSectionRoute() {
  const context = useOutletContext<SettingsOutletContext>();
  const { section: slug } = useParams();
  const section = settingsSection(slug);

  if (!section || (section.adminOnly && !context.isAdmin)) {
    return <Navigate to={settingsPath(context.schoolSlug)} replace />;
  }
  // A section that moved: the old link lands on where it went.
  if (section.slug !== slug) {
    return <Navigate to={settingsPath(context.schoolSlug, section.slug)} replace />;
  }

  return (
    <>
      {/* Without the rail, the overview is how you get back out. */}
      <Link
        to={settingsPath(context.schoolSlug)}
        className="mb-5 -ml-1 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground lg:hidden"
      >
        <ChevronLeft className="h-4 w-4" />
        All settings
      </Link>

      {section.slug === 'account' ? (
        <AccountSettings />
      ) : (
        <div className="max-w-3xl">
          <SettingsPanelHeader title={section.label} blurb={section.blurb} />
          {section.usesSettingsQuery && context.loading ? (
            <SettingsLoading />
          ) : section.usesSettingsQuery && context.error ? (
            <p className="text-sm text-destructive">
              These settings could not be loaded: {context.error}. Reload the page to try again.
            </p>
          ) : (
            <SectionBody slug={section.slug} context={context} />
          )}
        </div>
      )}
    </>
  );
}

function SectionBody({ slug, context }: { slug: string; context: SettingsOutletContext }) {
  const { app, schoolId, data, refetch } = context;
  const { school, roleContext, setSelection, refetchSchool } = app;
  const schoolYears = school?.schoolYears ?? [];

  switch (slug) {
    case 'school-years':
      return (
        <SchoolYearsEditor
          schoolId={schoolId}
          schoolYears={schoolYears}
          selectedSchoolYearId={roleContext?.selectedSchoolYear?.id}
          onSchoolYearCreated={(schoolYear) => {
            refetchSchool();
            refetch();
            if (schoolYear) setSelection({ schoolYearId: schoolYear.id });
          }}
          onCurrentYearChanged={() => {
            refetchSchool();
            refetch();
          }}
        />
      );

    case 'grade-levels':
      return (
        <GradeLevelsEditor
          schoolId={schoolId}
          setting={data?.gradeLevelsSetting}
          onSaved={refetch}
        />
      );

    case 'classrooms':
      return <ClassroomsEditor schoolId={schoolId} />;

    case 'grade-scale':
      return (
        <GradeScaleEditor
          schoolId={schoolId}
          entries={data?.gradeScaleSetting?.entries}
          onSaved={refetch}
        />
      );

    case 'grading-templates':
      return <GradingTemplatesEditor schoolId={schoolId} onChanged={refetch} />;

    case 'tuition-and-fees':
      return <TuitionAndFees years={data?.schoolYears ?? []} onSaved={refetch} />;

    case 'enrollment-checklists':
      return <EnrollmentChecklists schoolId={schoolId} schoolName={context.schoolName} />;

    case 'payments':
      return <PaymentsTab schoolId={schoolId} stripeAccount={data?.stripeAccount} onRefetch={refetch} />;

    case 'parent-alerts':
      return (
        <ParentAlertsEditor
          schoolId={schoolId}
          setting={data?.parentAlertsSetting}
          gradeScaleEntries={data?.gradeScaleSetting?.entries}
          onSaved={refetch}
        />
      );

    default:
      return null;
  }
}

export default SettingsSectionRoute;
